import { useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { IPafApplication, IListAdmin, brokerApi } from '../../_api';
import { useAuth } from '../../_auth/useAuth';

/**
 * Custom hook to handle broker auto-fill functionality
 * Decouples broker data fetching and form population logic
 */
export const useBrokerAutoFill = (setValue: UseFormSetValue<IPafApplication>) => {
    const { getAccessToken } = useAuth();

    /**
     * Populates PAF form fields with broker data
     * @param listAdmin - The broker data to populate
     */
    const populateBrokerFields = useCallback((listAdmin: IListAdmin): void => {
        const options = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
        // Flatten company fields if present
        const company = (listAdmin as any).company || {};
        setValue("listadmin_id" as any, listAdmin.listadmin_id ?? null, options);
        setValue("listadmin__listadmin_sid" as any, listAdmin.listadmin_sid ?? '', options);
        setValue("listadmin__company_name" as any, company.company_name ?? listAdmin.company_name ?? '', options);
        setValue("listadmin__address1" as any, company.address1 ?? listAdmin.address1 ?? '', options);
        setValue("listadmin__address2" as any, company.address2 ?? listAdmin.address2 ?? '', options);
        setValue("listadmin__city" as any, company.city ?? listAdmin.city ?? '', options);
        setValue("listadmin__state" as any, company.state ?? listAdmin.state ?? '', options);
        setValue("listadmin__postal_code" as any, company.postal_code ?? listAdmin.postal_code ?? '', options);
        setValue("listadmin__naics_code" as any, company.naics_code ?? listAdmin.naics_code ?? '', options);
       
        // Representative fields: name/email from user, title from title
        const user = (listAdmin as any).user || {};
        setValue("listadmin__phone" as any, user?.phone ?? '', options);
        setValue("listadmin__representative_name" as any, (user.first_name || '') + (user.last_name ? (' ' + user.last_name) : ''), options);
        setValue("listadmin__representative_email" as any, user.email || '', options);
        setValue("listadmin__representative_title" as any, listAdmin.title ?? '', options);
        setValue("listadmin__company_website" as any, company.company_website ?? '', options);
    }, [setValue]);

    /**
     * Fetches broker data by SID and auto-fills PAF form fields
     * @param sid - The broker's SID (e.g., "S1310")
     * @returns Promise that resolves when auto-fill is complete
     */
    const autoFillBrokerBySid = useCallback(async (sid: string): Promise<boolean> => {
        try {
            const auth_token = await getAccessToken();
            const resp = await brokerApi.get_by_sid({ sid }, auth_token);
            
            if (resp && resp.success && resp.data) {
                populateBrokerFields(resp.data);
                return true;
            }
            
            return false;
        } catch (error) {
            return false;
        }
    }, [getAccessToken, populateBrokerFields]);

    /**
     * Fetches broker data by ID and auto-fills PAF form fields
     * @param id - The broker's ID
     * @returns Promise that resolves when auto-fill is complete
     */
    const autoFillBrokerById = useCallback(async (id: number): Promise<boolean> => {
        try {
            const auth_token = await getAccessToken();
            const resp = await brokerApi.get_broker_details_by_id({ id }, auth_token);
            
            if (resp && resp.success && resp.data) {
                populateBrokerFields(resp.data);
                return true;
            }
            
            return false;
        } catch (error) {
            return false;
        }
    }, [getAccessToken, populateBrokerFields]);

    return {
        autoFillBrokerBySid,
        autoFillBrokerById
    };
};
